"use client";

import clsx from "clsx";
import React from "react";
import { DropDownBase, DropDownItem, OnDropDownItemSelect } from "../DropDown/DropDown";
import styles from "./select.module.scss";

export type SelectOption<ValueType> = Omit<DropDownItem<ValueType>, "selected">;
export type OnSelectValueChanged<ValueType> = (value: ValueType) => void;

interface State
{
    empty: boolean;
    focused: boolean;
    hasPlaceholder: boolean;
    open: boolean;
}

export interface SelectProps<ValueType>
{
    dropDownOpen?: boolean;
    onChange?: OnSelectValueChanged<ValueType>;
    options: SelectOption<ValueType>[];
    placeholder?: string;
    value?: ValueType;
}

export const Select = <ValueType,>({ dropDownOpen, onChange, options, placeholder, value }: SelectProps<ValueType>): React.FunctionComponentElement<SelectProps<ValueType>> =>
{
    const updateStateByProps = React.useCallback((state: State, dropDownOpen: SelectProps<ValueType>["dropDownOpen"], placeholder: SelectProps<ValueType>["placeholder"], value: SelectProps<ValueType>["value"]): State =>
    {
        const empty = value === undefined;
        const hasPlaceholder = placeholder !== undefined && placeholder.trim() !== "";
        const open = dropDownOpen ?? false;
        return state.empty !== empty || state.hasPlaceholder !== hasPlaceholder || state.open !== open ? { ...state, empty, hasPlaceholder, open } : state;
    }, []);

    const [state, setState] = React.useState<State>(() => updateStateByProps(
        {
            empty: true,
            focused: false,
            hasPlaceholder: false,
            open: false
        },
        dropDownOpen,
        placeholder,
        value
    ));

    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() =>
    {
        setState(prevState => updateStateByProps(prevState, dropDownOpen, placeholder, value));
    }, [dropDownOpen, placeholder, updateStateByProps, value]);

    const handleBlurred = React.useCallback<React.FocusEventHandler<HTMLInputElement>>(_ =>
    {
        const focused = false;
        const open = false;
        setState(prevState => prevState.focused !== focused || prevState.open !== open ? { ...prevState, focused, open } : prevState);
    }, []);

    const handleClicked = React.useCallback<React.MouseEventHandler<HTMLInputElement>>(event =>
    {
        event.preventDefault();
        event.stopPropagation();
        setState(prevState =>
        {
            const open = !prevState.open;
            const focused = !prevState.focused;

            /*const inputElement = inputRef.current;
            if (inputElement !== null)
            {
                console.log("inputElement", inputElement, prevState.focused);
                if (prevState.focused)
                {
                    inputElement.focus();

                }
                else
                {
                    inputElement.blur();
                }
            }*/
            return { ...prevState, open, focused };
        });
    }, []);

    const handleKeyDowned = React.useCallback<React.KeyboardEventHandler<HTMLInputElement>>(event =>
    {
        event.preventDefault();
        event.stopPropagation();

        if (event.code === "Space")
        {
            setState(prevState => prevState.focused ? { ...prevState, open: !prevState.open } : prevState);
        }
    }, []);

    const handleFocused = React.useCallback<React.FocusEventHandler<HTMLInputElement>>(_ =>
    {
        setState(prevState =>
        {
            const focused = true;
            const open = true;
            return prevState.focused !== focused || prevState.open !== open ? { ...prevState, focused, open } : prevState;
        });
    }, []);

    const getProps = React.useCallback((onChange: OnSelectValueChanged<ValueType> | undefined, options: SelectOption<ValueType>[], value: ValueType | undefined) =>
    {
        let valueNode: string | undefined = undefined;

        const items = options.map<DropDownItem<ValueType>>(e =>
        {
            const selected = e.value === value;
            if (selected)
            {
                valueNode = e.title;
            }
            return ({ ...e, selected });
        });

        const onItemSelect: OnDropDownItemSelect<ValueType> = item =>
        {
            if (onChange !== undefined)
            {
                const newValue = item.value;
                if (value !== newValue)
                {
                    onChange(newValue);
                }
            }
        };

        return {
            dropDownProps: { items, onItemSelect },
            valueNode
        };
    }, []);

    const { dropDownProps, valueNode } = getProps(onChange, options, value);

    return (
        <div className={clsx(styles["select"], { open: state.open })} onClick={handleClicked} >
            <input aria-hidden="true" onBlur={handleBlurred} onFocus={handleFocused} onKeyDown={handleKeyDowned} ref={inputRef} />
            <div className={styles["value"]}>
                {valueNode}
            </div>
            <div className={styles["trigger"]} >
                <svg width="22" height="12" viewBox="0 0 22 12" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.4228 9.84611L21.375 1.48367L19.9038 0.109375L11.1597 8.26438L2.42956 0.109375L0.958336 1.48367L11.1597 11.0259L12.4228 9.84611Z" />
                </svg>
            </div>
            <div className={styles["drop-down-wrapper"]}>
                <DropDownBase  {...dropDownProps} />
            </div>
        </div>
    );
}
